import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Star,
  ShieldCheck,
  Award,
  MapPin,
  Phone,
  Home as HomeIcon,
  Wrench,
  CloudRain,
  Workflow,
  LayoutGrid,
  Utensils,
  Bath,
  SearchCheck,
  Camera,
  Sun,
  Flame,
  FileCheck,
} from "lucide-react";
import {
  COMPANY,
  PRIMARY_SERVICES,
  SECONDARY_SERVICES,
  ROOFING_SYSTEMS,
  REVIEWS,
  STATS,
  CLIENT_PHOTOS,
} from "../data/content";

const ICONS = {
  Home: HomeIcon,
  Wrench,
  CloudRain,
  Workflow,
  LayoutGrid,
  Utensils,
  Bath,
  SearchCheck,
  Camera,
  Sun,
  Flame,
  FileCheck,
};

const HERO_IMAGE = CLIENT_PHOTOS.aerialPeaked;

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section
        className="relative flex flex-col md:justify-end min-h-[100svh] overflow-hidden grain"
        data-testid="hero-section"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Braintree Massachusetts roof replacement"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a192f]/95 via-[#0a192f]/75 to-[#0a192f]/55" />
        </div>

        <div className="container-x relative z-10 pt-28 md:pt-40 pb-12 md:pb-32 w-full">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="eyebrow eyebrow-line text-[var(--copper)] reveal">
                Braintree, MA · Est. {COMPANY.founded}
              </div>
              <h1 className="font-serif-display text-white text-4xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[1.04] md:leading-[1.02] tracking-tight mt-6 reveal reveal-delay-1">
                Braintree's Premier
                <br />
                Roofing Specialists.
                <br />
                <span className="italic font-light">30+ Years Strong.</span>
              </h1>
              <p className="text-white/80 text-lg md:text-xl max-w-xl mt-8 leading-relaxed reveal reveal-delay-2">
                Roof replacements, repairs and inspections done right the first time —
                serving Braintree and the South Shore. Additional home improvement
                services available.
              </p>
              <div className="mt-10 flex flex-wrap gap-4 reveal reveal-delay-3">
                <Link
                  to="/contact"
                  data-testid="hero-cta-estimate"
                  className="btn-copper"
                >
                  Get a Free Roof Estimate
                  <ArrowUpRight size={16} />
                </Link>
                <Link
                  to="/gallery"
                  data-testid="hero-cta-gallery"
                  className="btn-outline-light"
                >
                  See Roofing Projects
                </Link>
              </div>
            </div>

            <div className="md:col-span-4 reveal reveal-delay-4">
              <div className="bg-white/5 border border-white/15 backdrop-blur-md p-8">
                <div className="eyebrow mb-3">Why Admiral</div>
                <ul className="space-y-4 text-white/90 text-sm">
                  <li className="flex items-start gap-3">
                    <ShieldCheck className="shrink-0 text-[var(--copper)]" size={18} />
                    <span>Licensed, insured & locally owned in Braintree.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Award className="shrink-0 text-[var(--copper)]" size={18} />
                    <span>GAF & CertainTeed factory-certified roofing crews.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Star className="shrink-0 text-[var(--copper)]" size={18} />
                    <span>5-star reputation built over three decades.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Stat strip */}
        <div className="relative md:absolute md:bottom-0 left-0 right-0 z-10 border-t border-white/10 backdrop-blur-md bg-[#0a192f]/40">
          <div className="container-x grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {STATS.map((s) => (
              <div key={s.label} className="py-5 px-2 text-center" data-testid={`hero-stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="font-serif-display text-white text-2xl md:text-3xl">
                  {s.value}
                </div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/60 mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO BAND */}
      <section className="bg-[var(--bg-light)] py-20 md:py-28" data-testid="intro-band">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="eyebrow eyebrow-line mb-4">A Local Family Trade</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.1] text-[var(--navy)]">
              Three generations of South Shore roofers — still answering the phone in Braintree.
            </h2>
          </div>
          <div className="md:col-span-5 md:pl-10 md:border-l border-[var(--border-light)]">
            <p className="text-[var(--text-mute)] text-base md:text-lg leading-relaxed">
              Since {COMPANY.founded}, Admiral Roofing has handled everything from a
              single shingle repair to full roof replacements. Now based on Central
              Ave in Braintree, we serve the South Shore with the same crews, the
              same standards, and the same name on the truck.
            </p>
            <Link to="/about" data-testid="intro-about-link" className="link-copper mt-6">
              Our Story <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ROOFING SYSTEMS — materials expertise */}
      <section
        className="bg-white py-20 md:py-28 border-y border-[var(--border-light)]"
        data-testid="roofing-systems-section"
      >
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-7">
              <div className="eyebrow eyebrow-line mb-3">Roofing Systems</div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--navy)] leading-[1.05]">
                Six roofing systems.
                <br />
                <span className="italic font-light text-[var(--text-mute)]">
                  One crew that installs every one of them.
                </span>
              </h2>
            </div>
            <div className="md:col-span-5 md:pl-8 md:border-l border-[var(--border-light)]">
              <p className="text-[var(--text-mute)] text-base md:text-lg leading-relaxed">
                From standard architectural asphalt to hand-soldered copper and
                seamless EPDM — we&rsquo;ve been specifying, sourcing and
                installing the full range of New England roofing materials for
                over three decades.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border-light)] border border-[var(--border-light)]">
            {ROOFING_SYSTEMS.map((sys, i) => (
              <article
                key={sys.id}
                data-testid={`roofing-system-${sys.id}`}
                className="service-card bg-white p-8 md:p-10 flex flex-col"
              >
                <div className="flex items-baseline justify-between mb-6">
                  <div className="eyebrow">{String(i + 1).padStart(2, "0")}</div>
                  <div className="eyebrow text-[var(--copper)]">System</div>
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl text-[var(--navy)] leading-tight mb-2">
                  {sys.name}
                </h3>
                <p className="text-sm text-[var(--text-mute)] italic mb-6">{sys.tagline}</p>
                <ul className="mt-auto space-y-2 text-xs uppercase tracking-wider text-[var(--navy)]/80">
                  {sys.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <span className="w-3 h-px bg-[var(--copper)] mt-2 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PRIMARY: SERVICES GRID (roof-focused, exact order) */}
      <section
        className="bg-[var(--bg-light)] py-20 md:py-28 border-b border-[var(--border-light)]"
        data-testid="services-section"
      >
        <div className="container-x">
          <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-14">
            <div>
              <div className="eyebrow eyebrow-line mb-3">Services</div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--navy)] leading-[1.05] max-w-2xl">
                Everything your roof will ever need — in one crew.
              </h2>
            </div>
            <Link to="/contact" data-testid="services-cta-estimate" className="btn-copper self-start md:self-auto whitespace-nowrap">
              Free Roof Estimate
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border-light)] border border-[var(--border-light)]">
            {PRIMARY_SERVICES.map((s, idx) => {
              const Icon = ICONS[s.icon] || HomeIcon;
              return (
                <article
                  key={s.id}
                  data-testid={`service-card-${s.id}`}
                  className="service-card bg-white p-8 md:p-10 flex flex-col"
                >
                  <div className="flex items-start justify-between mb-8">
                    <Icon className="text-[var(--copper)]" size={28} />
                    <span className="font-serif-display italic text-sm text-[var(--text-mute)]">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="font-serif-display text-2xl md:text-[1.7rem] text-[var(--navy)] leading-snug mb-3">
                    {s.title}
                  </h3>
                  <p className="text-sm text-[var(--text-mute)] leading-relaxed mb-6">
                    {s.blurb}
                  </p>
                  <ul className="mt-auto space-y-2 text-xs uppercase tracking-wider text-[var(--navy)]/80">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-2">
                        <span className="w-3 h-px bg-[var(--copper)] mt-2 shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
            <div className="service-card bg-[var(--navy)] text-white p-8 md:p-10 flex flex-col justify-between">
              <div>
                <div className="eyebrow mb-4">Not Sure What You Need?</div>
                <h3 className="font-serif-display text-2xl md:text-3xl leading-snug">
                  Send us a photo of your roof. We&rsquo;ll come look — the inspection is free.
                </h3>
              </div>
              <Link
                to="/contact"
                data-testid="services-card-cta"
                className="btn-copper mt-8 self-start"
              >
                Book a Visit <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SECONDARY: KITCHEN & BATH + SIDING */}
      <section
        className="bg-white py-16 md:py-20 border-b border-[var(--border-light)]"
        data-testid="secondary-services-section"
      >
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <div className="eyebrow eyebrow-line mb-3">Also Available</div>
              <h3 className="font-serif-display text-2xl md:text-3xl text-[var(--navy)] leading-tight max-w-2xl">
                Additional Home Improvement Services.
              </h3>
              <p className="text-sm text-[var(--text-mute)] leading-relaxed max-w-xl mt-3">
                Beyond the roof — kitchen &amp; bath remodels and full siding
                projects for existing roofing clients on the South Shore.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--border-light)] border border-[var(--border-light)]">
            {SECONDARY_SERVICES.map((s) => {
              const Icon = ICONS[s.icon] || HomeIcon;
              return (
                <div
                  key={s.id}
                  data-testid={`secondary-service-${s.id}`}
                  className="bg-white p-6 md:p-8 flex flex-col gap-3 service-card"
                >
                  <Icon className="text-[var(--copper)]" size={22} />
                  <h4 className="font-serif-display text-xl md:text-2xl text-[var(--navy)] leading-snug">
                    {s.title}
                  </h4>
                  <p className="text-sm text-[var(--text-mute)] leading-relaxed">
                    {s.blurb}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section
        className="bg-[var(--bg-light)] py-20 md:py-32 overflow-hidden"
        data-testid="reviews-section"
      >
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-7">
              <div className="eyebrow eyebrow-line mb-3">Reviews</div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--navy)] leading-[1.05]">
                Three decades of word-of-mouth. From neighbors who watched the work happen.
              </h2>
            </div>
            <div className="md:col-span-5 flex md:justify-end items-end">
              <div className="text-[var(--text-mute)] text-sm leading-relaxed max-w-xs">
                Every project ends with a walkthrough, a written warranty, and a clean job site. That's why our reviews read like they do.
              </div>
            </div>
          </div>
        </div>

        <div className="relative">
          <div className="marquee-track">
            {[...REVIEWS, ...REVIEWS].map((r, idx) => (
              <article
                key={idx}
                data-testid={idx < REVIEWS.length ? `review-card-${idx}` : undefined}
                className="bg-white border border-[var(--border-light)] p-8 md:p-10 mx-4 w-[340px] md:w-[440px] shrink-0 flex flex-col"
              >
                <div className="flex gap-1 text-[var(--copper)] mb-5">
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <Star key={i} size={14} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <p className="font-serif-display text-base md:text-lg italic text-[var(--navy)] leading-relaxed mb-6 line-clamp-6">
                  &ldquo;{r.quote}&rdquo;
                </p>
                <div className="border-t border-[var(--border-light)] pt-5 mt-auto flex justify-between items-end">
                  <div>
                    <div className="font-bold text-sm text-[var(--navy)]">{r.name}</div>
                    {r.location && (
                      <div className="text-xs text-[var(--text-mute)] flex items-center gap-1.5 mt-1">
                        <MapPin size={11} /> {r.location}
                      </div>
                    )}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--copper)] text-right max-w-[40%]">
                    {r.project}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="container-x mt-16 md:mt-16 flex justify-center">
          <Link to="/gallery" data-testid="reviews-cta-gallery" className="btn-outline-navy whitespace-nowrap">
            See The Work Behind The Reviews
          </Link>
        </div>
      </section>

      {/* BIG CTA BAND */}
      <section
        className="bg-[var(--navy)] text-white py-20 md:py-28 relative overflow-hidden"
        data-testid="cta-band"
      >
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="eyebrow eyebrow-line mb-4">Free, no-pressure estimate</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
              Wondering what your roof actually needs?
              <br />
              <span className="text-white/60 italic font-light">
                We'll come tell you — straight.
              </span>
            </h2>
          </div>
          <div className="md:col-span-4 flex flex-col gap-4 md:items-end">
            <a
              href={`tel:${COMPANY.phoneTel}`}
              data-testid="cta-band-phone"
              className="text-white text-2xl font-serif-display flex items-center gap-3"
            >
              <Phone size={20} className="text-[var(--copper)]" /> {COMPANY.phone}
            </a>
            <Link to="/contact" data-testid="cta-band-estimate" className="btn-copper">
              Get a Free Estimate
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
